/* ============================================================
   Cast London — Product page interactions v3.1 (1.0.10)
   Swatch selection · Image crossfade · Thumbnails
   Accordion · Sticky ATC · Zoom overlay
   v3.1: setHeroImage falls back to the finish-coded hero when a
   size-specific stem is absent (single-size photography, e.g. WB-002).
   ============================================================ */
'use strict';

class ProductPage {
  constructor() {
    this.form = document.querySelector('[data-product-form]');
    if (!this.form) return;

    /* Core */
    this.variantInput     = document.getElementById('product-variant-id');
    this.mainImg          = document.getElementById('product-main-image');
    this.priceEl          = document.querySelector('[data-product-price]');
    this.serialEl         = document.querySelector('[data-product-serial]');
    this.specSerialEl     = document.querySelector('[data-spec-serial]');
    this.dispatchEl       = document.querySelector('[data-dispatch]');
    this.selectedFinishEl = document.querySelector('[data-selected-finish]');
    this.atcBtn           = document.querySelector('[data-atc-btn]');
    this.tradeNoteEl      = document.querySelector('[data-trade-note]');
    this.swatches         = document.querySelectorAll('[data-swatch]');
    this.thumbs           = document.querySelectorAll('[data-thumb]');

    /* Multi-axis products (>1 real option) emit a variant map; resolution matches
       on the full option name→value set. A single-option product (default "Title",
       or Finish-only) emits no map → multiAxis false → the untouched single-axis
       path. Size is tracked separately only to pick the image-swap helper so the
       basin's Finish+Size behaviour is unchanged. */
    this.sizeButtons      = document.querySelectorAll('[data-size]');
    this.hasSizeAxis      = this.sizeButtons.length > 0;
    this.selectedSizeEl   = document.querySelector('[data-selected-size]');
    this.variantMap       = this._parseVariantMap();   // [] for single-option products
    this.multiAxis        = this.variantMap.length > 0;
    this.optionNames      = this.multiAxis ? Object.keys(this.variantMap[0].opts || {}) : [];
    this.current          = { sizeToken: '', finishCode: '', optsByName: {} };
    if (this.multiAxis) this._seedOpts();

    /* Sticky ATC */
    this.stickyAtc        = document.querySelector('[data-sticky-atc]');
    this.stickyAtcBtn     = document.querySelector('[data-sticky-atc-btn]');
    this.stickyFinishEl   = document.querySelector('[data-sticky-finish]');
    this.stickyPriceEl    = document.querySelector('[data-sticky-price]');

    /* Zoom */
    this.zoomOverlay      = document.querySelector('[data-zoom-overlay]');
    this.zoomImg          = document.querySelector('[data-zoom-img]');
    this.zoomOpenBtn      = document.querySelector('[data-zoom-open]');
    this.zoomCloseBtn     = document.querySelector('[data-zoom-close]');
    this.zoomPrevBtn      = document.querySelector('[data-zoom-prev]');
    this.zoomNextBtn      = document.querySelector('[data-zoom-next]');
    this.zoomCounter      = document.querySelector('[data-zoom-counter]');

    /* FST finish note */
    this.fstFinishNote    = document.querySelector('[data-fst-finish-note]');

    /* Quantity controls */
    this.qtyInput = this.form.querySelector('.quantity__input');
    this.qtyMinus = this.form.querySelector('[data-qty-minus]');
    this.qtyPlus  = this.form.querySelector('[data-qty-plus]');

    /* Map finish code → hero image (parsed from media filename stems) */
    this.buildFinishImages();

    /* Ordered list of all gallery images, for lightbox browsing */
    this.buildGalleryImages();

    this.initSwatches();
    this.initThumbs();
    this.initQuantity();
    this.initAccordion();
    this.initStickyAtc();
    this.initZoom();
    this.initSpecTabs();

    if (this.hasSizeAxis) this.initSizes();   // bind Size buttons (basin)
    this.initOptionSwatches();                // bind generic option swatches (e.g. Fonte Head/Controls); no-op if none

    if (this.multiAxis) {
      const hasVariantParam = new URL(window.location.href).searchParams.has('variant');
      if (hasVariantParam) {
        /* Deep link: the server marked the right variant active + led with its
           media. Ensure the image matches (no-op if already correct). */
        this._syncVariantImage(this._findCurrentVariant());
        this._updatePairedCards(this.current.finishCode);
      } else {
        /* Plain load: leave the server-rendered lead IMAGE untouched (re-setting
           would flash), but preselect the variant whose media the server led with,
           so control state + price + serial follow the shown image (1.0.11 §9). */
        const leadThumb = document.querySelector('[data-thumb].thumb-btn--active');
        const leadId    = leadThumb ? leadThumb.dataset.mediaId : null;
        const v = leadId ? this.variantMap.find((x) => String(x.media) === String(leadId)) : null;
        if (v) {
          this._preselectFromVariant(v);
        } else {
          /* No variant owns the lead media (e.g. basin -pair shot) → today's behaviour */
          this._updatePairedCards(this.current.finishCode);
        }
      }
    } else {
      /* Single-axis (no variant map): show the active finish hero, unchanged */
      this.applyInitialFinishImage();
    }
  }

  /* Seed current option selection (name→value) + the finish code / size token used
     by the image-swap helpers, from whichever controls are active on load. */
  _seedOpts() {
    const af = document.querySelector('[data-swatch].finish-swatch-btn--active');
    if (af) { this.current.optsByName['Finish'] = af.dataset.finishName; this.current.finishCode = af.dataset.finishCode; }
    if (this.hasSizeAxis) {
      const asz = document.querySelector('[data-size].size-btn--active') || this.sizeButtons[0];
      if (asz) { this.current.optsByName['Size'] = asz.dataset.sizeName; this.current.sizeToken = asz.dataset.sizeValue; }
    }
    document.querySelectorAll('[data-option-btn].option-swatch-btn--active').forEach((b) => {
      this.current.optsByName[b.dataset.optionName] = b.dataset.optionValue;
    });
  }

  /* ---- Spec / Documentation tabs -------------------------- */
  /* Peer tabs, not an accordion. No-op when there's only one tab
     (Documentation is suppressed server-side when no PDF exists). */
  initSpecTabs() {
    const root = document.querySelector('[data-spec]');
    if (!root) return;
    const tabs   = root.querySelectorAll('[data-spec-tab]');
    const panels = root.querySelectorAll('[data-spec-panel]');
    if (tabs.length < 2) return;

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const target = tab.dataset.specTab;
        tabs.forEach((t) => {
          const on = t === tab;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        panels.forEach((p) => {
          const on = p.dataset.specPanel === target;
          p.classList.toggle('is-active', on);
          p.hidden = !on;
        });
      });
    });
  }

  /* ---- Variant map (two-axis) ----------------------------- */
  _parseVariantMap() {
    const el = document.querySelector('[data-variant-map]');
    if (!el) return [];
    try { return JSON.parse(el.textContent); } catch (e) { return []; }
  }

  /* ---- Finish → hero image map ---------------------------- */
  /* Filenames encode the finish code, e.g. arco-shower-cbz-hero.jpg.
     CB's hero is the irregular arco-shower-cb-1-hero.jpg, and the brass
     detail shots (cb-2-angle …) share the "cb" token — so we key off a
     hyphen-bounded code AND require "hero" in the stem to isolate the hero. */
  buildFinishImages() {
    this.finishImages = {};
    /* Longest codes first so CBZ wins over CB; hyphen-bounded = whole token */
    const CODE_RE = /-(cbz|cb|cn|ck|cg|cs)-/;

    this.thumbs.forEach((thumb) => {
      const src = thumb.dataset.src;
      if (!src) return;
      const stem = this.fileStem(src);
      if (!stem || stem.indexOf('hero') === -1) return;

      const match = stem.match(CODE_RE);
      if (!match) return;

      const code = match[1].toUpperCase();
      if (!this.finishImages[code]) {
        this.finishImages[code] = {
          src,
          srcset: thumb.dataset.srcset || '',
          thumb,
        };
      }
    });
  }

  /* Filename stem from a (possibly CDN-query-stringed) URL, lowercased.
     e.g. ".../arco-shower-cb-1-hero.jpg?v=12&width=1200" → "arco-shower-cb-1-hero" */
  fileStem(url) {
    if (!url) return '';
    try {
      const path = new URL(url, window.location.href).pathname;
      const file = path.slice(path.lastIndexOf('/') + 1);
      return file.replace(/\.[a-z0-9]+$/i, '').toLowerCase();
    } catch (e) {
      return '';
    }
  }

  /* Swap the main image to a finish's hero, falling back to the supplied
     variant image when that finish has no coded hero media. */
  setFinishImage(code, fallbackSrc, fallbackSrcset) {
    const hero   = code ? this.finishImages[code] : null;
    const src    = hero ? hero.src    : fallbackSrc;
    const srcset = hero ? hero.srcset : fallbackSrcset;
    if (!src) return;

    /* Skip the crossfade if that image is already showing (avoids a flash
       when the server already rendered the correct hero on deep-link) */
    const current = this.mainImg ? this.fileStem(this.mainImg.currentSrc || this.mainImg.src) : '';
    if (current !== this.fileStem(src)) {
      this.setMainImage(src, srcset);
    }

    /* Keep the active thumbnail in sync with the shown hero */
    if (hero && hero.thumb) {
      this.thumbs.forEach((t) => t.classList.remove('thumb-btn--active'));
      hero.thumb.classList.add('thumb-btn--active');
    }
  }

  /* On load, sync the gallery to whichever swatch is active (handles
     ?variant= deep-links even if the variant has no featured image set). */
  applyInitialFinishImage() {
    const active = document.querySelector('[data-swatch].finish-swatch-btn--active')
                || document.querySelector('[data-swatch][aria-pressed="true"]');
    if (!active) return;
    this.setFinishImage(
      active.dataset.finishCode,
      active.dataset.variantImage,
      active.dataset.variantImageSrcset
    );
    this._updatePairedCards(active.dataset.finishCode);
  }

  /* ---- Finish swatches ------------------------------------ */
  initSwatches() {
    this.swatches.forEach((s) => {
      s.addEventListener('click', () => this.selectSwatch(s));
    });
  }

  selectSwatch(swatch) {
    this._setSwatchActive(swatch);

    /* Multi-axis (map present): a finish alone doesn't identify the variant —
       update this axis and resolve via the full option name→value match. */
    if (this.multiAxis) {
      this.current.optsByName['Finish'] = swatch.dataset.finishName;
      this.current.finishCode           = swatch.dataset.finishCode;
      this.resolveVariant();
      return;
    }

    /* Single-axis (unchanged): the swatch carries its own variant data + image */
    this._applyVariantUI({
      variantId:  swatch.dataset.variantId,
      price:      swatch.dataset.variantPrice,
      dispatch:   swatch.dataset.variantDispatch,
      available:  swatch.dataset.variantAvailable === 'true',
      finishName: swatch.dataset.finishName,
      finishCode: swatch.dataset.finishCode,
      isSlate:    swatch.dataset.isSlate === 'true',
    });
    this.setFinishImage(swatch.dataset.finishCode, swatch.dataset.variantImage, swatch.dataset.variantImageSrcset);
  }

  /* Toggle the active finish swatch (active class + aria-pressed) */
  _setSwatchActive(swatch) {
    this.swatches.forEach((s) => {
      s.classList.remove('finish-swatch-btn--active');
      s.setAttribute('aria-pressed', 'false');
    });
    swatch.classList.add('finish-swatch-btn--active');
    swatch.setAttribute('aria-pressed', 'true');
  }

  /* Shared variant → UI update, used by both single-axis (selectSwatch) and
     multi-axis (resolveVariant) paths so they can't drift. Does NOT touch the
     image (callers handle that, since the swap rule differs per axis). */
  _applyVariantUI(d) {
    if (this.variantInput && d.variantId != null) this.variantInput.value = d.variantId;

    const priceAmount = this.priceEl?.querySelector('[data-price-amount]');
    if (priceAmount && d.price != null) priceAmount.textContent = d.price;

    if (this.selectedFinishEl && d.finishName != null) this.selectedFinishEl.textContent = d.finishName;
    if (this.selectedSizeEl   && d.sizeName   != null) this.selectedSizeEl.textContent   = d.sizeName;

    /* Dispatch (inventory-driven): "In stock" or the variant's lead time */
    if (this.dispatchEl && d.dispatch != null) this.dispatchEl.textContent = d.dispatch;

    const base       = this.serialEl?.dataset.serialBase || this.specSerialEl?.dataset.serialBase || '';
    const serialText = d.finishCode ? `${base}-${d.finishCode}` : base;
    if (this.serialEl)     this.serialEl.textContent     = serialText;
    if (this.specSerialEl) this.specSerialEl.textContent = serialText;

    const inStock = this.atcBtn?.dataset.labelInStock || 'Add to cart';
    const soldOut = this.atcBtn?.dataset.labelSoldOut || 'Sold out';
    if (this.atcBtn) {
      this.atcBtn.disabled    = !d.available;
      this.atcBtn.textContent = d.available ? inStock : soldOut;
    }
    if (this.stickyAtcBtn) {
      this.stickyAtcBtn.disabled    = !d.available;
      this.stickyAtcBtn.textContent = d.available ? inStock : soldOut;
    }
    if (this.stickyFinishEl && d.finishName != null) this.stickyFinishEl.textContent = d.finishName;
    if (this.stickyPriceEl  && d.price      != null) this.stickyPriceEl.textContent  = d.price;

    if (this.tradeNoteEl) this.tradeNoteEl.classList.toggle('is-visible', !!d.isSlate);

    if (this.fstFinishNote) {
      this.fstFinishNote.textContent = d.finishCode ? `Specified in ${d.finishName}` : '';
    }

    /* URL sync — no reload */
    if (d.variantId != null) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', d.variantId);
      window.history.replaceState({}, '', url.toString());
    }

    /* "Completes the set" — paired cards inherit the selected finish */
    this._updatePairedCards(d.finishCode);
  }

  /* Swap each paired-card image ([data-finish-img] in "Completes the set") to the
     active finish's hero, falling back to its featured image. No-op when there are
     no paired cards, so this is inert on products without the section. */
  _updatePairedCards(code) {
    if (this.pairedImgs == null) this.pairedImgs = document.querySelectorAll('[data-finish-img]');
    if (!this.pairedImgs.length) return;
    const c = (code || '').toLowerCase();
    this.pairedImgs.forEach((img) => {
      if (img._finishMap === undefined) {
        try { img._finishMap = JSON.parse(img.getAttribute('data-finish-images') || '{}'); }
        catch (e) { img._finishMap = {}; }
      }
      const url = (c && img._finishMap[c]) ? img._finishMap[c] : img.getAttribute('data-featured');
      if (url) {
        /* A srcset overrides src (the browser resolves currentSrc from srcset), so
           setting src alone never changes the displayed image. These cards are small
           — drop the responsive srcset and drive them by src. Applies to both the
           mapped-finish and data-featured fallback paths. */
        if (img.hasAttribute('srcset')) img.removeAttribute('srcset');
        if (img.getAttribute('src') !== url) img.src = url;
      }

      /* Card href carries the selected finish (?variant=), kept in sync with the
         swapped image. Falls back to the bare product URL when this companion has
         no variant in the current finish. */
      const a = img.closest('a');
      if (a) {
        if (img._finishVars === undefined) {
          try { img._finishVars = JSON.parse(img.getAttribute('data-finish-variants') || '{}'); }
          catch (e) { img._finishVars = {}; }
        }
        if (a._hrefBase === undefined) a._hrefBase = (a.getAttribute('href') || '').split('?')[0];
        const vid = c && img._finishVars[c];
        a.setAttribute('href', vid ? `${a._hrefBase}?variant=${vid}` : a._hrefBase);
      }
    });
  }

  /* ---- Size selector (two-axis) --------------------------- */
  initSizes() {
    this.sizeButtons.forEach((b) => b.addEventListener('click', () => this.selectSize(b)));
  }

  selectSize(btn) {
    this.sizeButtons.forEach((b) => {
      b.classList.remove('size-btn--active');
      b.setAttribute('aria-pressed', 'false');
    });
    btn.classList.add('size-btn--active');
    btn.setAttribute('aria-pressed', 'true');
    this.current.optsByName['Size'] = btn.dataset.sizeName;
    this.current.sizeToken          = btn.dataset.sizeValue;
    this.resolveVariant();
  }

  /* ---- Generic option swatches (any option beyond Finish/Size) ---- */
  initOptionSwatches() {
    document.querySelectorAll('[data-option-btn]').forEach((btn) => {
      btn.addEventListener('click', () => this.selectOption(btn));
    });
  }

  selectOption(btn) {
    const name = btn.dataset.optionName;
    /* Activate this value within its own option group only */
    document.querySelectorAll(`[data-option-btn][data-option-name="${CSS.escape(name)}"]`).forEach((b) => {
      const on = b === btn;
      b.classList.toggle('option-swatch-btn--active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    this.current.optsByName[name] = btn.dataset.optionValue;   // raw value, exact
    const head = document.querySelector(`[data-selected-option="${CSS.escape(name)}"]`);
    if (head) head.textContent = btn.dataset.optionValue;
    this.resolveVariant();
  }

  /* Find the variant matching the full current option set (name→value map, not
     by position; raw values compared exactly — no handleize / case-fold). */
  _findCurrentVariant() {
    return this.variantMap.find(
      (x) => this.optionNames.every((n) => x.opts[n] === this.current.optsByName[n])
    );
  }

  /* Resolve the variant, update the UI, and swap the gallery. */
  resolveVariant() {
    const v = this._findCurrentVariant();

    this._applyVariantUI({
      variantId:  v ? v.id : null,
      price:      v ? v.price : null,
      dispatch:   v ? v.dispatch : null,
      available:  v ? v.available : false,
      finishName: this.current.optsByName['Finish'] || null,
      finishCode: this.current.finishCode,
      sizeName:   this.current.optsByName['Size'] || null,
      isSlate:    false,
    });

    /* Stale-id guard: no combination match → clear the hidden id so a wrong
       variant can't be added (ATC is already disabled via available:false). */
    if (!v && this.variantInput) this.variantInput.value = '';

    this._syncVariantImage(v);
  }

  /* Swap the gallery to the resolved variant's own media when it has one (exact —
     the merchandiser's assignment, e.g. WC-001 Round plates). Otherwise fall back
     to the existing stem helpers, unchanged. 1.0.11 — buildFinishImages / the
     finish regex are untouched; this only adds a media-first preference. */
  _syncVariantImage(v) {
    if (v && v.media != null) {
      const thumb = document.querySelector(`[data-thumb][data-media-id="${v.media}"]`);
      if (thumb) {
        this.thumbs.forEach((t) => t.classList.remove('thumb-btn--active'));
        thumb.classList.add('thumb-btn--active');
        const cur = this.mainImg ? this.fileStem(this.mainImg.currentSrc || this.mainImg.src) : '';
        if (cur !== this.fileStem(thumb.dataset.src || '')) {
          this.setMainImage(thumb.dataset.src, thumb.dataset.srcset);
        }
        return;
      }
    }
    /* Fallback: today's finish/size stem path (no-op when finish/size unchanged) */
    if (this.hasSizeAxis) this.setHeroImage(this.current.sizeToken, this.current.finishCode);
    else                  this.setFinishImage(this.current.finishCode);
  }

  /* Plain-load preselect (1.0.11, brief §9): align the control state + variant id
     + price/serial to the variant the server already led with, WITHOUT touching
     the image (it's correct; re-setting would flash). Mirrors resolveVariant's
     state update minus the image. */
  _preselectFromVariant(v) {
    this.current.optsByName = Object.assign({}, v.opts);

    /* Finish swatch */
    const fName = v.opts['Finish'];
    let finishCode = '';
    this.swatches.forEach((s) => {
      const on = s.dataset.finishName === fName;
      s.classList.toggle('finish-swatch-btn--active', on);
      s.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on) finishCode = s.dataset.finishCode;
    });
    this.current.finishCode = finishCode;

    /* Size button */
    const sName = v.opts['Size'];
    this.sizeButtons.forEach((b) => {
      const on = b.dataset.sizeName === sName;
      b.classList.toggle('size-btn--active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on) this.current.sizeToken = b.dataset.sizeValue;
    });

    /* Generic option buttons (Buttons/Head/Controls/…) + their selected labels */
    document.querySelectorAll('[data-option-btn]').forEach((b) => {
      const on = v.opts[b.dataset.optionName] === b.dataset.optionValue;
      b.classList.toggle('option-swatch-btn--active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on) {
        const head = document.querySelector(`[data-selected-option="${CSS.escape(b.dataset.optionName)}"]`);
        if (head) head.textContent = b.dataset.optionValue;
      }
    });

    /* State update (hidden id, price, serial, dispatch, sticky, URL, paired cards) — NO image */
    this._applyVariantUI({
      variantId:  v.id,
      price:      v.price,
      dispatch:   v.dispatch,
      available:  v.available,
      finishName: fName || null,
      finishCode: finishCode,
      sizeName:   sName || null,
      isSlate:    false,
    });
  }

  /* Two-axis image swap: the hero whose stem ends -{size}-{finish}-hero.
     The size token disambiguates short vs tall (e.g. -short-cb-hero ≠
     -tall-cb-hero ≠ -short-cbz-hero). */
  setHeroImage(sizeToken, finishCode) {
    if (!sizeToken || !finishCode) return;
    const suffix = `-${sizeToken}-${finishCode.toLowerCase()}-hero`;

    let hero = null;
    for (const thumb of this.thumbs) {
      if (this.fileStem(thumb.dataset.src || '').endsWith(suffix)) {
        hero = { src: thumb.dataset.src, srcset: thumb.dataset.srcset || '', thumb };
        break;
      }
    }
    /* v3.1 (1.0.10): no size-specific stem for this combination — fall back to
       the finish-coded hero (any size). Products shot in a single size (WB-002)
       keep a working finish swap on the other size; products with per-size
       photography (BB-001) never reach this branch. */
    if (!hero) {
      const fb = this.finishImages[finishCode.toUpperCase()];
      if (!fb) return;
      hero = fb;
    }

    const current = this.mainImg ? this.fileStem(this.mainImg.currentSrc || this.mainImg.src) : '';
    if (current !== this.fileStem(hero.src)) this.setMainImage(hero.src, hero.srcset);

    this.thumbs.forEach((t) => t.classList.remove('thumb-btn--active'));
    hero.thumb.classList.add('thumb-btn--active');
  }

  /* ---- Lightbox gallery list ------------------------------ */
  /* Ordered hi-res list of every gallery image, built from the thumbnail
     strip so the lightbox can browse the full set (generic, no per-product
     code). Single-image products fall back to the main image. */
  buildGalleryImages() {
    this.galleryImages = [];
    this.thumbs.forEach((thumb) => {
      if (!thumb.dataset.src) return;
      const img = thumb.querySelector('img');
      this.galleryImages.push({
        src: thumb.dataset.src,
        srcset: thumb.dataset.srcset || '',
        alt: (img && img.alt) || '',
      });
    });
    if (!this.galleryImages.length && this.mainImg) {
      this.galleryImages.push({
        src: this.mainImg.src,
        srcset: this.mainImg.srcset || '',
        alt: this.mainImg.alt || '',
      });
    }
    this.zoomIndex = 0;
  }

  /* ---- Thumbnails ----------------------------------------- */
  initThumbs() {
    this.thumbs.forEach((t) => {
      t.addEventListener('click', () => this.selectThumb(t));
    });
  }

  selectThumb(thumb) {
    this.thumbs.forEach((t) => t.classList.remove('thumb-btn--active'));
    thumb.classList.add('thumb-btn--active');
    const src    = thumb.dataset.src;
    const srcset = thumb.dataset.srcset;
    if (src) this.setMainImage(src, srcset);
  }

  /* ---- Image crossfade ------------------------------------ */
  setMainImage(src, srcset) {
    if (!this.mainImg) return;

    this.mainImg.classList.add('is-fading');

    const preload  = new Image();
    preload.onload = () => {
      this.mainImg.src    = src;
      if (srcset) this.mainImg.srcset = srcset;
      this.mainImg.classList.remove('is-fading');
      /* Keep zoom image in sync */
      if (this.zoomImg && this.zoomOverlay?.classList.contains('is-open')) {
        this.zoomImg.src = src;
      }
    };
    preload.onerror = () => {
      this.mainImg.classList.remove('is-fading');
    };
    if (srcset) preload.srcset = srcset;
    preload.src = src;
  }

  /* ---- Quantity controls ---------------------------------- */
  initQuantity() {
    if (!this.qtyInput) return;

    this.qtyMinus?.addEventListener('click', () => {
      const val = parseInt(this.qtyInput.value, 10) || 1;
      if (val > 1) this.qtyInput.value = val - 1;
    });

    this.qtyPlus?.addEventListener('click', () => {
      const val = parseInt(this.qtyInput.value, 10) || 1;
      this.qtyInput.value = val + 1;
    });
  }

  /* ---- Accordion ------------------------------------------ */
  initAccordion() {
    const btns = document.querySelectorAll('[data-accordion-btn]');
    btns.forEach((btn) => {
      btn.addEventListener('click', () => this.toggleAccordion(btn));
    });
  }

  toggleAccordion(btn) {
    const item   = btn.closest('.accordion-item');
    const isOpen = item.classList.contains('is-open');

    /* Close all open panels */
    document.querySelectorAll('.accordion-item.is-open').forEach((openItem) => {
      openItem.classList.remove('is-open');
      openItem.querySelector('[data-accordion-btn]').setAttribute('aria-expanded', 'false');
    });

    /* Open the clicked one if it was closed */
    if (!isOpen) {
      item.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
    }
  }

  /* ---- Sticky ATC ----------------------------------------- */
  initStickyAtc() {
    if (!this.stickyAtc || !this.atcBtn) return;

    /* Sticky btn submits the main form */
    this.stickyAtcBtn?.addEventListener('click', () => {
      document.getElementById('product-form')?.requestSubmit();
    });

    /* Show sticky bar when main ATC scrolls out of view */
    const observer = new IntersectionObserver(
      ([entry]) => {
        const show = !entry.isIntersecting;
        this.stickyAtc.classList.toggle('is-visible', show);
        this.stickyAtc.setAttribute('aria-hidden', String(!show));
        /* Hide the floating basket pill while the sticky bar covers that corner */
        document.body.classList.toggle('sticky-atc-open', show);
      },
      { threshold: 0, rootMargin: '0px 0px -60px 0px' }
    );

    observer.observe(this.atcBtn);
  }

  /* ---- Zoom / lightbox gallery ---------------------------- */
  initZoom() {
    if (!this.zoomOverlay) return;

    /* Open on zoom button or main image click */
    this.zoomOpenBtn?.addEventListener('click', () => this.openZoom());

    const galleryMain = document.querySelector('[data-gallery-main]');
    galleryMain?.addEventListener('click', (e) => {
      if (e.target === this.mainImg || e.target === galleryMain) this.openZoom();
    });

    /* Prev / next controls */
    this.zoomPrevBtn?.addEventListener('click', () => this.zoomStep(-1));
    this.zoomNextBtn?.addEventListener('click', () => this.zoomStep(1));

    /* Close — overlay/image click, but ignore the click a swipe emits */
    this.zoomCloseBtn?.addEventListener('click', () => this.closeZoom());
    this.zoomOverlay.addEventListener('click', (e) => {
      if (this._zoomSwiped) return;
      if (e.target === this.zoomOverlay || e.target === this.zoomImg) this.closeZoom();
    });

    /* Keyboard — Esc closes, ←/→ navigate (only while open) */
    document.addEventListener('keydown', (e) => {
      if (!this.zoomOverlay.classList.contains('is-open')) return;
      if (e.key === 'Escape') {
        this.closeZoom();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.zoomStep(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.zoomStep(1);
      }
    });

    /* Touch — horizontal swipe navigates (swipe left → next) */
    let startX = null, startY = null;
    this.zoomOverlay.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) { startX = null; return; }
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });
    this.zoomOverlay.addEventListener('touchend', (e) => {
      if (startX === null) return;
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      startX = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        this._zoomSwiped = true;
        setTimeout(() => { this._zoomSwiped = false; }, 350);
        this.zoomStep(dx < 0 ? 1 : -1);
      }
    }, { passive: true });
  }

  openZoom() {
    if (!this.zoomImg || !this.mainImg || !this.galleryImages.length) return;

    /* Start on whatever the gallery is currently showing — this respects a
       finish swatch having swapped the main image to a finish hero. */
    const currentStem = this.fileStem(this.mainImg.currentSrc || this.mainImg.src);
    let idx = this.galleryImages.findIndex((g) => this.fileStem(g.src) === currentStem);
    if (idx < 0) idx = 0;
    this.zoomIndex = idx;

    this.renderZoom();
    this.zoomOverlay.classList.add('is-open');
    this.zoomOverlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.zoomCloseBtn?.focus();
  }

  /* Paint the current lightbox image + control state */
  renderZoom() {
    const img = this.galleryImages[this.zoomIndex];
    if (!img) return;

    this.zoomImg.src = img.src;
    if (img.srcset) this.zoomImg.srcset = img.srcset;
    else this.zoomImg.removeAttribute('srcset');
    this.zoomImg.alt = img.alt;

    const multiple = this.galleryImages.length > 1;
    if (this.zoomPrevBtn) this.zoomPrevBtn.hidden = !multiple;
    if (this.zoomNextBtn) this.zoomNextBtn.hidden = !multiple;
    if (this.zoomCounter) {
      this.zoomCounter.hidden = !multiple;
      this.zoomCounter.textContent = `${this.zoomIndex + 1} / ${this.galleryImages.length}`;
    }
  }

  /* Move through the lightbox gallery with wrap-around */
  zoomStep(delta) {
    const n = this.galleryImages.length;
    if (n < 2) return;
    this.zoomIndex = (this.zoomIndex + delta + n) % n;
    this.renderZoom();
  }

  closeZoom() {
    this.zoomOverlay.classList.remove('is-open');
    this.zoomOverlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.zoomOpenBtn?.focus();
  }
}

document.addEventListener('DOMContentLoaded', () => new ProductPage());
